import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { AGUViewportService } from '../viewport/phone';
import { SelectPlacements, type AGUAdKind, type AGUAdPlacement } from './ad-placement';

/** The MJ user setting holding the next Home feature rotation index (the prototype's `localStorage.agu_fx_rot`, P/Member_Hub.html:1515). */
export const AD_FEATURE_INDEX_SETTING = 'agu.hub.ads.featureIndex';

/**
 * Where the rotation index persists across sessions. The surface supplies it (MJExplorer backs it with `UserInfoEngine`, a
 * `MJ: User Settings` row) so this package stays free of browser storage and its specs need no MJ engine.
 */
export interface AGUAdRotationStore {
    /** The stored value of `AD_FEATURE_INDEX_SETTING`, or undefined when there is none. */
    Load(): Promise<string | undefined>;
    /** Persists the value; may debounce. */
    Save(value: string): void;
}

export const AGU_AD_ROTATION_STORE = new InjectionToken<AGUAdRotationStore>('AGUAdRotationStore');

/**
 * The Featured placements' per-session state (PD §3): which creatives the portal config enabled, which the member dismissed
 * (the prototype's `sessionStorage.agu_fx_<id>`, P/Member_Hub.html:1359-1360, held in memory instead), and the Home feature's
 * rotation index.
 *
 * Provided by each surface component, never `providedIn: 'root'` — like `AGUViewportService` — so a dismissal lasts as long
 * as the surface instance and two surfaces open at once keep their own. Every slot asks `Select`/`First`, which answer nothing
 * while ads are disabled or the viewport is a phone (`html.mob [class^="fx-"]{display:none}`, P/Member_Hub.html:530).
 */
@Injectable()
export class AGUAdSessionService {
    private enabled = false;
    private placements: readonly AGUAdPlacement[] = [];
    private readonly dismissed = new Set<string>();
    private storedIndex: number | null = null;
    private featureIndex: number | null = null;

    constructor(
        @Optional() @Inject(AGUViewportService) private readonly viewport: AGUViewportService | null,
        @Optional() @Inject(AGU_AD_ROTATION_STORE) private readonly store: AGUAdRotationStore | null
    ) {}

    /** Sets what the portal config allows (`adsEnabled`, `adPlacements`). */
    Configure(enabled: boolean, placements: readonly AGUAdPlacement[]): void {
        this.enabled = enabled;
        this.placements = placements;
    }

    /** True when slots may render: ads enabled and not on a phone. */
    get Active(): boolean {
        return this.enabled && !(this.viewport?.IsPhone ?? false);
    }

    /** The creatives of `kind` for `screen` (see `SelectPlacements`), or none while inactive. */
    Select(screen: string, kind: AGUAdKind): AGUAdPlacement[] {
        return this.Active ? SelectPlacements(this.placements, screen, kind, this.dismissed) : [];
    }

    /** The first creative of `kind` for `screen`, or null. */
    First(screen: string, kind: AGUAdKind): AGUAdPlacement | null {
        return this.Select(screen, kind)[0] ?? null;
    }

    /** Hides a creative for the rest of this surface session (P/Member_Hub.html:1366-1374). */
    Dismiss(id: string): void {
        this.dismissed.add(id);
    }

    IsDismissed(id: string): boolean {
        return this.dismissed.has(id);
    }

    /** Reads the stored rotation index before Home asks for it; fail-silent (any failure starts the rotation at 0). */
    async PrimeRotation(): Promise<void> {
        try {
            const raw = await this.store?.Load();
            const n = raw === undefined ? NaN : Number.parseInt(raw, 10);
            this.storedIndex = Number.isFinite(n) && n >= 0 ? n : 0;
        } catch {
            this.storedIndex = 0;
        }
    }

    /**
     * The Home feature's rotation index for this session: the stored value (0 when unread or unusable), with the next value
     * written back once so the following session shows the next creative (P/Member_Hub.html:1515). Held for the session, so
     * returning to Home in the same surface keeps the same creative — the prototype rotates per page load, not per visit.
     */
    NextFeatureIndex(): number {
        if (this.featureIndex !== null) return this.featureIndex;
        this.featureIndex = this.storedIndex ?? 0;
        try {
            this.store?.Save(String(this.featureIndex + 1));
        } catch {
            // Fail-silent: an unsaved index only repeats this creative next session.
        }
        return this.featureIndex;
    }
}
